import { memo } from "react"
import { FiSearch } from "react-icons/fi"

import { ClassSessionActionsMenu } from "@/components/layout/Common/ClassSessionActionsMenu"
import type { ClassSessionResponse } from "@/types/class_session"
import { EmptyState, Flex, Table, VStack } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface ClassSessionListProps {
  classSessions: ClassSessionResponse[]
}

const ClassSessionList = memo(({ classSessions }: ClassSessionListProps) => {
  if (!classSessions?.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No classSessions yet</EmptyState.Title>
            <EmptyState.Description>
              Create a new classSession to see it listed here.
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>Class Template Id</Table.ColumnHeader>
          <Table.ColumnHeader>Instructor Id</Table.ColumnHeader>
          <Table.ColumnHeader>Session Date</Table.ColumnHeader>
          <Table.ColumnHeader>Session Start</Table.ColumnHeader>
          <Table.ColumnHeader>Session End</Table.ColumnHeader>
          <Table.ColumnHeader>Status</Table.ColumnHeader>
          <Table.ColumnHeader>Cancellation Reason</Table.ColumnHeader>
          <Table.ColumnHeader>Location Override</Table.ColumnHeader>
          <Table.ColumnHeader>Max Students Override</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {classSessions.map((classSession) => (
          <Table.Row key={classSession.id}>
            <Table.Cell truncate maxW="sm">
              {classSession.id}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.class_template_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.instructor_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.session_date, "date")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.session_start, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.session_end, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.status, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.cancellation_reason, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.location_override, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.max_students_override, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.notes, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classSession.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <ClassSessionActionsMenu classSession={classSession} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

ClassSessionList.displayName = "ClassSessionList"

export default ClassSessionList
